import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  input,
  NgZone,
  OnDestroy,
  OnInit,
  output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { MembersViewAvailableFilters } from '@app/app/page-modules/admin/components/members/admin-member-control-panel/admin-member-control-panel.component';
import { AssessmentExportDialogComponent } from '@app/app/page-modules/admin/components/members/admin-member-control-panel/assessment-export-dialog/assessment-export-dialog.component';
import { ImportUsersDialogComponent } from '@app/app/page-modules/admin/components/members/admin-member-control-panel/import-users-dialog/import-users-dialog.component';
import { AssessmentExportData, MembersListColumns } from '@app/app/page-modules/admin/components/members/model/members.model';
import {
  UpdateCardFilters,
  UpdateMemberFilters,
  UpdatePlaylistFilters,
} from '@app/app/page-modules/admin/components/members/store/members.action';
import { MembersState } from '@app/app/page-modules/admin/components/members/store/members.state';
import { CardFilter, PlaylistFilter } from '@app/app/page-modules/admin/components/members/store/members.state.model';
import { MEMBERS_DATA_SERVICE, MembersDataService } from '@app/app/page-modules/admin/services/members/members-data.service';
import { LTI_COURSE_DATA_SERVICE, LtiCourseDataService } from '@app/app/page-modules/lti/services/lti-course-data.service';
import { SubmissionMode } from '@app/app/page-modules/playlist/models/submission-logic.model';
import { PlaylistResourceEditorPublicationState } from '@app/app/page-modules/playlist/store/create/publication/playlist-publication.state';
import { PlaylistViewState } from '@app/app/page-modules/playlist/store/view/playlist-view.state';
import { ResourceAdminState } from '@app/app/page-modules/resource/store/admin/resource-admin.state';
import { RedirectHelper } from '@app/app/page-modules/resource/store/editor/content/helpers/redirect.helper';
import { ContentHelper } from '@app/app/shared/helpers/content-helper';
import { DialogService } from '@app/app/shared/helpers/dialog/dialog.service';
import { EventCardsHelper } from '@app/app/shared/helpers/event-cards-helper';
import { LanguageCodeHelper } from '@app/app/shared/helpers/language-code-helper';
import { Organization, Playlist, PlaylistPublicationSettings, Resource, UserSubmissionMode } from '@app/app/shared/models';
import {
  AndRequest,
  BooleanFilter,
  BooleanFilterType,
  BooleanQueryType,
  MembersBooleanSearchRequest,
  OrRequest,
} from '@app/app/shared/models/admin/boolean-filters.model';
import { CardHeaders, DistanceTravelMode, DistanceTravelRequest, FormHeaders } from '@app/app/shared/models/admin/members.model';
import { LoadableState, ObservableResult } from '@app/app/shared/store';
import { UserAuthState } from '@app/app/user-auth/store/user-auth.state';
import { LucideDownload, LucideFunnel, LucideSearch, LucideUpload, LucideX } from '@lucide/angular';
import { TranslocoService } from '@ngneat/transloco';
import { Select, Store } from '@ngxs/store';
import * as moment from 'moment/moment';
import { combineLatest, debounceTime, distinctUntilChanged, filter, Observable, Subject, Subscription, takeUntil } from 'rxjs';

@Component({
  selector: 'ptl-v3-admin-member-control-panel',
  templateUrl: './v3-admin-member-control-panel.component.html',
  styleUrls: ['./v3-admin-member-control-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V3AdminMembersControlPanelComponent implements OnInit, OnDestroy {
  columns = input<MembersListColumns[]>();
  isAdminPage = input<boolean>();
  isPlaylistPage = input<boolean>();
  isCardPage = input<boolean>();
  isAssessment = input<boolean>();
  isEvent = input<boolean>();
  totalMembersCount = input<number>();
  allMembersCount = input<number>();
  booleanSearchRequest = input<MembersBooleanSearchRequest>();
  playlistUid = input<string>();
  cardUid = input<string>();
  exportEnabled = input<boolean>();
  exportName = input<string>();
  isWaitingListEnabled = input<boolean>();
  cardHeaders = input<CardHeaders[]>();
  formHeaders = input<FormHeaders[]>();
  eventFinished = input<boolean>();
  isAllSelected = input<boolean>();
  isWaitingListOperationDisabled = input<boolean>();
  courseUid = input<string>();

  reviewersChanged = output<void>();
  filterChanged = output<{ andRequest: AndRequest; resetPageNumber: boolean }>();
  csvFileUploadFinished = output<number>();
  triggerUserImport = output<{ file: File; headers: string[] }>();
  refreshList = output<number>();

  @Select(UserAuthState.organizationDetails)
  organizationData$: Observable<Organization>;

  @Select(UserAuthState.canManageUserImportPrivileges)
  canManageUserImportPrivileges$: Observable<boolean>;

  @Select(ResourceAdminState.resource)
  private resource$: Observable<Resource>;

  @Select(MembersState.memberFilters)
  private filters$: Observable<BooleanFilter[]>;

  @Select(MembersState.cardFilters)
  private cardFilters$: Observable<CardFilter[]>;

  @Select(MembersState.playlistFilters)
  private playlistFilters$: Observable<PlaylistFilter[]>;

  @Select(UserAuthState.userHasSuperAdminRole)
  private isUserSuperAdmin$: Observable<boolean>;

  @Select(UserAuthState.userHasAdminRole)
  private isUserAdmin$: Observable<boolean>;

  @Select(PlaylistResourceEditorPublicationState.publicationSettings)
  private playlistPublicationSettings$: Observable<PlaylistPublicationSettings>;

  @Select(PlaylistViewState.playlistState)
  private playlist$: Observable<LoadableState<Playlist>>;

  filters: BooleanFilter[] = [];
  currentFilter: MembersViewAvailableFilters;
  newFilter: MembersViewAvailableFilters;
  filterLabels: { type: MembersViewAvailableFilters; value: string }[];
  nameRequest: OrRequest;

  exportStarted: boolean;
  advancedExportAvailable = false;
  importDisabledTooltipText: string;

  cardPublished = false;
  playlistPublished = false;
  limitedTickets = false;
  remainingTickets: number;
  ticketsQuantity: number;
  remainingTicketsInfoClass: string = undefined;
  isIframeMode: boolean;
  isInitialStateLoad = true;
  isImportDisabled = false;
  canManageReviews = false;

  private importUsersDialogRef: MatDialogRef<ImportUsersDialogComponent>;
  private assessmentExportUsersDialogRef: MatDialogRef<AssessmentExportDialogComponent>;
  private filtersSubscription: Subscription;
  private nameSubscription: Subscription;
  private nameSubject$ = new Subject<void>();

  private subscriptionEnd$ = new EventEmitter<void>();
  private contentSubscription: Subscription;
  private organizationDataSubscription: Subscription;
  private playlistSubmissionMode: UserSubmissionMode;
  isTicketExist: boolean;
  searchControl = new FormControl('');

  protected readonly LucideSearch = LucideSearch;
  protected readonly LucideDownload = LucideDownload;
  protected readonly LucideUpload = LucideUpload;
  protected readonly LucideFunnel = LucideFunnel;
  protected readonly LucideX = LucideX;

  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private translocoService = inject(TranslocoService);
  private store = inject(Store);
  private dialog = inject(MatDialog);
  private dialogService = inject(DialogService);
  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  private membersDataService = inject<MembersDataService>(MEMBERS_DATA_SERVICE);
  private ltiCourseDataService = inject<LtiCourseDataService>(LTI_COURSE_DATA_SERVICE);

  constructor() {
    this.isIframeMode = ContentHelper.isFrameMode();
    this.nameSubscription = this.nameSubject$.pipe(debounceTime(500)).subscribe(() => this.applyFilters());
  }

  ngOnInit(): void {
    combineLatest([this.isUserAdmin$, this.isUserSuperAdmin$])
      .pipe(takeUntil(this.subscriptionEnd$))
      .subscribe((data) => {
        this.advancedExportAvailable = this.checkIfUserCanViewExports(data[0], data[1]);
      });
    this.searchControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.subscriptionEnd$))
      .subscribe((value) => this.nameUpdated(value || ''));
    this.playlist$.pipe(takeUntil(this.subscriptionEnd$)).subscribe((playlist) => {
      if (playlist && playlist.data) {
        if (this.isPlaylistPage()) {
          const reviewType = playlist?.data?.userPlaylistSubmissionSummary?.userReviewSummary?.reviewType;
          this.canManageReviews = reviewType && reviewType !== 'AUTOMATIC';
        }
        this.playlistSubmissionMode = playlist?.data?.userPlaylistSubmissionSummary?.submissionMode;
        this.isTicketExist = !!playlist?.data?.publicationSettings?.enrollment?.actionTrigger?.configuration?.tickets;
      }
    });

    this.playlistPublicationSettings$.pipe(takeUntil(this.subscriptionEnd$)).subscribe((publicationSettings) => {
      if (publicationSettings) {
        this.playlistPublished = publicationSettings.published;
        if (this.playlistPublished) {
          this.importDisabledTooltipText = undefined;
        } else {
          this.importDisabledTooltipText = this.translocoService.translate(
            'translations.membersNew.controlPanel.import.importDisabledTooltipTextPlaylist',
          );
        }
        this.isImportDisabled = !this.playlistPublished;
        this.cdr.detectChanges();
      }
    });
    if (this.isCardPage()) {
      this.contentSubscription = this.resource$.subscribe((resource) => {
        if (resource) {
          this.cardPublished = resource.status === 'PUBLISHED';
          this.canManageReviews = resource.reviewType && resource.reviewType !== 'AUTOMATIC';
          if (this.cardPublished) {
            this.importDisabledTooltipText = undefined;
          } else {
            this.importDisabledTooltipText = this.translocoService.translate(
              'translations.membersNew.controlPanel.import.importDisabledTooltipText',
            );
          }
          if (resource.tickets?.availability === 'LIMITED') {
            this.limitedTickets = true;
            this.remainingTickets = resource.tickets.remainingTickets;
            this.ticketsQuantity = resource.tickets.quantity;
            this.remainingTicketsInfoClass = EventCardsHelper.getTextColorClassBasedOnRemainingTickets(resource.tickets);
          }
          this.isImportDisabled = !this.cardPublished;
          this.cdr.detectChanges();
        }
      });
    }
    this.buildAvailableFilters();
    this.initFilters();
  }

  ngOnDestroy(): void {
    this.contentSubscription?.unsubscribe();
    this.organizationDataSubscription?.unsubscribe();
    this.nameSubscription?.unsubscribe();
    this.filtersSubscription?.unsubscribe();
    this.subscriptionEnd$.emit();
  }

  showConfirmDialog(key) {
    const keyLowerCase = key.toLowerCase();
    const upperCaseFirstLetter = keyLowerCase.charAt(0).toUpperCase() + keyLowerCase.slice(1);
    const message = this.translocoService.translate('translations.dialog.title.reviewsBulk' + upperCaseFirstLetter);
    const config = {};
    return this.dialogService.showConfirmDialog(message, this.translocoService, config);
  }

  buildAvailableFilters(): void {
    const filterLabels = [];
    if (this.columnsExists(['GROUPS']) && !this.isIframeMode) {
      filterLabels.push({ type: 'GROUP', value: this.translocoService.translate('translations.reviews.filter.group') });
    }
    if (this.columnsExists(['ROLES'])) {
      filterLabels.push({ type: 'ROLE', value: this.translocoService.translate('translations.filter.role.name') });
    }
    if (this.columnsExists(['WILL_ATTEND', 'HAS_ATTENDED']) || (this.columnsExists(['ENROL']) && this.isTicketExist)) {
      filterLabels.push({ type: 'ATTENDANCE', value: this.translocoService.translate('translations.filter.attendance.name') });
    }
    if (this.columnsExists(['LAST_ACTIVE'])) {
      filterLabels.push({ type: 'ACTIVITY', value: this.translocoService.translate('translations.filter.activity.nameAlt') });
    }
    if (this.columnsExists(['REVIEW_STATUS'])) {
      filterLabels.push({ type: 'REVIEW_STATUS', value: this.translocoService.translate('translations.filter.review.name') });
    }
    if (!this.isEvent()) {
      filterLabels.push({ type: 'COMPLETION', value: this.translocoService.translate('translations.filter.completion.name') });
    }
    this.filterLabels = filterLabels;
  }

  columnsExists(columnsToVerify: MembersListColumns[]): boolean {
    const cols = this.columns();
    if (cols) {
      return columnsToVerify.every((c) => cols.includes(c));
    }
    return false;
  }

  filterSelected(filterType: BooleanFilterType): void {
    this.currentFilter = filterType as MembersViewAvailableFilters;
    if (!this.filters.map((f) => f.type).includes(this.currentFilter)) {
      this.newFilter = this.currentFilter;
    }
  }

  nameUpdated(name: string | OrRequest) {
    if (typeof name === 'string') {
      const trimmed = name.trim();
      if (trimmed) {
        const firstNameRequest = {
          type: BooleanQueryType.FIRST_NAME,
          firstName: trimmed,
        };
        const lastNameRequest = {
          type: BooleanQueryType.LAST_NAME,
          lastName: trimmed,
        };
        const externalIdRequest = {
          type: BooleanQueryType.EXTERNAL_ID,
          externalId: trimmed,
        };
        const emailRequest = {
          type: BooleanQueryType.EMAIL,
          email: trimmed,
        };
        const request = {
          type: BooleanQueryType.OR,
          args: [firstNameRequest, lastNameRequest, externalIdRequest, emailRequest],
        } as OrRequest;

        this.nameRequest = request as OrRequest;
      } else {
        this.nameRequest = undefined;
      }
    } else {
      this.nameRequest = name;
    }
    this.nameSubject$.next();
  }

  onRemoveFilter(type: MembersViewAvailableFilters): void {
    this.currentFilter = undefined;
    this.newFilter = undefined;
    this.filters = this.filters.filter((f) => f.type !== type);
    this.updateFilters();
  }

  onSaveFilter(f: BooleanFilter): void {
    this.currentFilter = undefined;
    this.newFilter = undefined;
    this.filters = this.filters.filter((currentFilter) => currentFilter.type !== f.type);
    this.filters = [...this.filters, f].sort((f1, f2) => {
      const order = ['GROUP', 'ROLE', 'ATTENDANCE', 'REVIEW_STATUS', 'ACTIVITY', 'COMPLETION'];
      const idx1 = order.findIndex((t) => f1.type === t);
      const idx2 = order.findIndex((t) => f2.type === t);
      return idx1 - idx2;
    });
    this.updateFilters();
  }

  private updateFilters(): void {
    this.isInitialStateLoad = false;

    if (this.isCardPage()) {
      this.store.dispatch(new UpdateCardFilters(this.cardUid(), this.filters));
    } else if (this.isPlaylistPage()) {
      this.store.dispatch(new UpdatePlaylistFilters(this.playlistUid(), this.filters));
    } else {
      this.store.dispatch(new UpdateMemberFilters(this.filters));
    }
  }

  private applyFilters(): void {
    const args = this.filters.flatMap((f) => f.request);
    if (this.nameRequest) {
      args.push(this.nameRequest);
    }
    const request = {
      type: BooleanQueryType.AND,
      args: args,
    } as AndRequest;

    this.filterChanged.emit({ andRequest: request, resetPageNumber: !this.isInitialStateLoad });
  }

  private initFilters(): void {
    if (this.isCardPage()) {
      this.filtersSubscription = this.cardFilters$.pipe(filter((data) => !!data)).subscribe((cardFilters) => {
        this.filters = cardFilters.find((cardFilter) => cardFilter.cardUid === this.cardUid())?.value ?? [];

        this.applyFilters();
      });
    } else if (this.isPlaylistPage()) {
      this.filtersSubscription = this.playlistFilters$.pipe(filter((data) => !!data)).subscribe((playlistFilters) => {
        this.filters = playlistFilters.find((playlistFilter) => playlistFilter.playlistUid === this.playlistUid())?.value ?? [];

        this.applyFilters();
      });
    } else {
      this.filtersSubscription = this.filters$.pipe(filter((data) => !!data)).subscribe((filters) => {
        this.filters = filters;

        this.applyFilters();
      });
    }
  }

  exportUsersButtonClicked(): void {
    if (this.isAssessment()) {
      this.ngZone.run(() => {
        this.assessmentExportUsersDialogRef = this.dialog.open(AssessmentExportDialogComponent, {
          width: '90vw',
          minWidth: '15.625rem',
          maxWidth: '46.875rem',
          maxHeight: '80vh',
          restoreFocus: true,
          position: {
            top: '10vh',
          },
          direction: LanguageCodeHelper.getBodyLanguageDir(),
          panelClass: 'ptl-mat-dialog',
          backdropClass: 'dialog-backdrop',
        });
        this.assessmentExportUsersDialogRef.componentInstance.exportCalled.subscribe((value) => this.exportUsers(value));
      });
    } else {
      this.exportUsers();
    }
  }

  openAdvancedExport(): void {
    RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/admin/advanced-export');
  }

  exportUsers(assessmentExportData?: AssessmentExportData): void {
    const request = this.getExportRequestData();
    this.exportStarted = true;
    let membersObservable: ObservableResult<string>;
    if (this.isPlaylistPage()) {
      membersObservable = this.membersDataService.booleanExportPlaylistMembers(request, this.playlistUid());
    } else if (this.isCardPage()) {
      if (this.isAssessment() && assessmentExportData?.distanceTravelEnabled) {
        if (assessmentExportData.firstStartDate && assessmentExportData.firstEndDate) {
          request.distanceTravelled = this.initializeIntervalsIfEmpty(request);
          request.distanceTravelled.includePercentage = assessmentExportData.includePercentage;
          request.distanceTravelled.intervals.push({
            startDate: this.formatDate(assessmentExportData.firstStartDate),
            endDate: this.formatDate(assessmentExportData.firstEndDate),
            mode: DistanceTravelMode[assessmentExportData.firstType],
          });
        }
        if (assessmentExportData.secondStartDate && assessmentExportData.secondEndDate) {
          request.distanceTravelled = this.initializeIntervalsIfEmpty(request);
          request.distanceTravelled.includePercentage = assessmentExportData.includePercentage;
          request.distanceTravelled.intervals.push({
            startDate: this.formatDate(assessmentExportData.secondStartDate),
            endDate: this.formatDate(assessmentExportData.secondEndDate),
            mode: DistanceTravelMode[assessmentExportData.secondType],
          });
        }
        membersObservable = this.membersDataService.booleanExportCardMembers(request, this.playlistUid(), this.cardUid());
      } else if (this.isEvent()) {
        membersObservable = this.membersDataService.booleanExportEventCardMembers(request, this.playlistUid(), this.cardUid());
      } else {
        membersObservable = this.membersDataService.booleanExportCardMembers(request, this.playlistUid(), this.cardUid());
      }
    } else {
      membersObservable = this.membersDataService.booleanExportMembers(request);
    }

    membersObservable.subscribe(({ isSuccess, value }) => {
      if (isSuccess) {
        this.downloadExportedFile(value);
      }
      this.exportStarted = false;
    });
  }

  private downloadExportedFile(value): void {
    const name = this.exportName() || 'members';
    const dateFormat = this.getExportedDateFormat();
    const exportedFileName = name + '_export_' + dateFormat + '.csv';
    const csvContent = 'data:application/octet-stream;base64,' + btoa(unescape(encodeURIComponent(value)));
    const link = document.createElement('a');
    link.id = 'csvButton';
    link.setAttribute('href', csvContent);
    link.setAttribute('target', '_blank');
    link.setAttribute('download', exportedFileName);
    document.body.appendChild(link); // Required for FF
    link.click();
    link.parentNode.removeChild(link);
  }

  exportUsersFilteredByGroup(): void {
    this.exportStarted = true;
    this.ltiCourseDataService.exportPlaylistMembers(this.playlistUid(), this.courseUid()).subscribe(({ isSuccess, value }) => {
      if (isSuccess) {
        this.downloadExportedFile(value);
      }
      this.exportStarted = false;
    });
  }

  private checkIfUserCanViewExports(isAdmin: boolean, isSuperAdmin: boolean): boolean {
    return isAdmin || isSuperAdmin;
  }

  private initializeIntervalsIfEmpty(request: MembersBooleanSearchRequest): DistanceTravelRequest {
    if (!request.distanceTravelled) {
      return (request.distanceTravelled = { intervals: [] });
    } else {
      return request.distanceTravelled;
    }
  }

  private formatDate(date: Date): string {
    const dateString = date.toString();
    const timezone = dateString.toString().substr(dateString.indexOf('GMT') + 3, 5);
    return moment(date).utcOffset(timezone, true).format('YYYY-MM-DD');
  }

  private getExportedDateFormat(): string {
    const date = new Date();
    return date.getFullYear() + '_' + (date.getMonth() + 1) + '_' + date.getDate() + '_' + date.getHours() + '_' + date.getMinutes();
  }

  private getExportRequestData(): MembersBooleanSearchRequest {
    return { ...this.booleanSearchRequest() };
  }

  importUsersCsv(): void {
    if ((this.isCardPage() && this.cardPublished) || (this.isPlaylistPage() && this.playlistPublished)) {
      this.ngZone.run(() => {
        this.importUsersDialogRef = this.dialog.open(ImportUsersDialogComponent, {
          width: '90vw',
          minWidth: '15.625rem',
          maxWidth: '46.875rem',
          maxHeight: '80vh',
          restoreFocus: true,
          position: {
            top: '10vh',
          },
          direction: LanguageCodeHelper.getBodyLanguageDir(),
          panelClass: 'ptl-mat-dialog',
          backdropClass: 'dialog-backdrop',
          data: {
            cardUid: this.cardUid(),
            playlistUid: this.playlistUid(),
            eventFinished: this.eventFinished(),
            isEvent: this.isEvent(),
            isCardPage: this.isCardPage(),
            isPlaylistPage: this.isPlaylistPage(),
          },
        });
        this.importUsersDialogRef.componentInstance.csvFileUploadFinished.subscribe((value) => this.csvFileUploadFinished.emit(value));
      });
    }
  }

  get getSubmissionMode(): SubmissionMode {
    if (this.isPlaylistPage()) {
      return this.playlistSubmissionMode;
    } else {
      return 'PROGRESSIVE';
    }
  }

  getRemainingTickets(): string {
    return this.remainingTickets.toString();
  }
}
